// src/lib/api.ts - Fixed for Vercel serverless
import { FinancialData, Project } from '../types';

// Always use relative path - let Vite handle proxying in dev, direct in prod
const getApiUrl = () => {
  return '/api';
};

const API_URL = getApiUrl();

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  details?: string;
}

interface InquiryData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  message?: string;
}

export const saveProjectToBackend = async (project: Project, authToken?: string): Promise<ApiResponse<Project>> => {
  try {
    console.log('Saving project to backend:', API_URL);
    
    const response = await fetch(`${API_URL}/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Pass authentication token if available
        ...(authToken && { 'Authorization': `Bearer ${authToken}` })
      },
      body: JSON.stringify(project)
    });
    
    if (!response.ok) {
      let errorMessage = 'Failed to save project';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        errorMessage = `${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error: any) {
    console.error('Error saving project:', error);
    return {
      success: false,
      error: error.message || 'Failed to save project'
    };
  }
};

export const updateProjectInBackend = async (
  projectId: string, 
  projectData: { financial_data: FinancialData; updated_at: string },
  authToken?: string
): Promise<ApiResponse<Project>> => {
  try {
    console.log('Updating project in backend:', API_URL);
    
    const response = await fetch(`${API_URL}/projects?projectId=${projectId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        // Pass authentication token if available
        ...(authToken && { 'Authorization': `Bearer ${authToken}` })
      },
      body: JSON.stringify(projectData)
    });
    
    if (!response.ok) {
      let errorMessage = 'Failed to update project';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        errorMessage = `${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error: any) {
    console.error('Error updating project:', error);
    return {
      success: false,
      error: error.message || 'Failed to update project'
    };
  }
};

export const submitBetaInquiry = async (data: InquiryData): Promise<ApiResponse<any>> => {
  try {
    console.log('Submitting beta inquiry to:', API_URL);
    
    const response = await fetch(`${API_URL}/beta-inquiry`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || 'Failed to submit inquiry');
    }
    
    return await response.json();
  } catch (error: any) {
    console.error('Error submitting beta inquiry:', error);
    return {
      success: false,
      error: error.message || 'Failed to submit inquiry'
    };
  }
};

export const submitGeneralInquiry = async (data: InquiryData): Promise<ApiResponse<any>> => {
  try {
    console.log('Submitting general inquiry to:', API_URL);
    
    const response = await fetch(`${API_URL}/general-inquiry`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || 'Failed to submit inquiry');
    }
    
    return await response.json();
  } catch (error: any) {
    console.error('Error submitting general inquiry:', error);
    return {
      success: false,
      error: error.message || 'Failed to submit inquiry'
    };
  }
};

// Add this new function to your existing api.ts file
export const updateProjectSettings = async (
  projectId: string,
  settings: { industry?: string; business_description?: string }
): Promise<ApiResponse<Project>> => {
  try {
    console.log('Updating project settings:', API_URL, projectId, settings);
    
    const response = await fetch(`${API_URL}/projects?projectId=${projectId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...settings,
        updated_at: new Date().toISOString()
      })
    });
    
    if (!response.ok) {
      let errorMessage = 'Failed to update project settings';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        errorMessage = `${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error: any) {
    console.error('Error updating project settings:', error);
    return {
      success: false,
      error: error.message || 'Failed to update project settings'
    };
  }
};